import { io } from "socket.io-client";
import {
  SOCKET_EVENTS,
  SOCKET_CLIENT_EVENTS,
} from "../constants/liveChatSocket";

// WABA Server Socket URL
const WABA_SOCKET_URL = process.env.REACT_APP_WABA_SERVER_URL;

let socket = null;

/**
 * Get user info from token
 */
const getUserInfoFromToken = () => {
  const token = localStorage.getItem("token");
  if (!token) return null;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      userId: payload.user_id,
      role: payload.role,
      platformAccountId: payload.platform_account_id,
      sections: payload.sections || [],
    };
  } catch (e) {
    return null;
  }
};

/**
 * Get livechat access level from user info
 */
const getLivechatAccessLevel = (userInfo) => {
  if (!userInfo) return null;
  if (userInfo.role === 'super_admin') return 'all';
  if (userInfo.sections.includes('livechats.all')) return 'all';
  if (userInfo.sections.includes('livechats.own')) return 'own';
  return null;
};

/**
 * Initialize Socket.IO connection with JWT authentication
 */
export const initSocket = () => {
  if (socket?.connected) {
    return socket;
  }

  const token = localStorage.getItem("token");
  const userInfo = getUserInfoFromToken();

  // Ensure we're connecting to the backend server, not frontend
  if (!WABA_SOCKET_URL) {
    console.error("REACT_APP_WABA_SERVER_URL is not set!");
    return null;
  }
  
  socket = io(WABA_SOCKET_URL, {
    path: "/socket.io",
    transports: ["websocket", "polling"],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: 5,
    // Pass token in auth object (preferred method for socket.io authentication)
    auth: {
      token: token,
    },
    // Also pass in headers for backward compatibility
    extraHeaders: {
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(userInfo?.userId && { "X-User-Id": userInfo.userId.toString() }),
    },
  });

  socket.on("connect", () => {
    console.log("🔌 Connected to chat server");
    // Emit user info for debugging
    if (userInfo) {
      console.log(`👤 User: ${userInfo.userId}, Role: ${userInfo.role}, Access: ${getLivechatAccessLevel(userInfo)}`);
    }
  });

  socket.on("disconnect", () => {
    console.log("❌ Disconnected from chat server");
  });

  socket.on("error", (error) => {
    console.error("Socket error:", error);
  });

  // Handle use_livechat_room event (server redirecting from user_room)
  socket.on(SOCKET_EVENTS.USE_LIVECHAT_ROOM, () => {
    socket.emit(SOCKET_CLIENT_EVENTS.JOIN_LIVECHAT_ROOM);
  });

  return socket;
};

/**
 * Get current socket instance
 */
export const getSocket = () => {
  if (!socket) {
    return initSocket();
  }
  return socket;
};

/**
 * Disconnect socket
 */
export const disconnectSocket = () => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};

/**
 * Get user info from current socket connection
 */
export const getSocketUserInfo = () => {
  return getUserInfoFromToken();
};

/**
 * Get livechat access level for current user
 */
export const getSocketAccessLevel = () => {
  const userInfo = getUserInfoFromToken();
  return getLivechatAccessLevel(userInfo);
};

const socketApi = { initSocket, getSocket, disconnectSocket, getSocketUserInfo, getSocketAccessLevel };
export default socketApi;

